import AppShellLayout from './AppShellLayout';
import React, { lazy, useState, useEffect, useMemo } from 'react';
import PageLoadBoundary from './PageLoadBoundary';
import { authFetch } from 'shared/api/authFetch';
import { config } from 'shared/config/config';
import { Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import AppSidebar from './Sidebar/AppSidebar';

import ConfigureSandboxPreviewChrome from '../../features/sandbox/components/ConfigureSandboxPreviewChrome';
import {
  legacyBranchReviewDestination,
} from '../../features/sandbox/utils/previewDestination';

import { AgentPanelProvider, AgentDock } from 'features/conversation';
import WorkspaceFirstEntry from 'features/conversation/WorkspaceFirstEntry';
import useWorkspaceEntry from './useWorkspaceEntry';
import { signOut } from '../../features/auth/api/authUtils';
import {
  canAccessAdminPage,
  getAdminShellPageFromPath,
  getAdminShellPathForPage,
  getDefaultAdminPageForRole,
  isPlatformConfigurator,
  useCurrentRoleApplicationAccess,
} from '../../features/auth/utils/roleRouting';
import { getLegacyPageFromPath } from '../router/routeManifest';
import { useOntologyViewsIndex } from '../../features/views/hooks/useOntologyViews';
import { useUserOrgId } from '../../shared/session/useUserOrgId';
import {
  ViewAsProvider, isViewAsFrame, useViewAs, viewerAccess,
} from '../../features/viewAs/ViewAsContext';
import ViewAsBanner from '../../features/viewAs/ViewAsBanner';
import ViewAsDeviceStage from '../../features/viewAs/ViewAsDeviceStage';
import { useAccountClaimStatus } from '../../features/accountManagement/accountClaim';
import { MakeItYoursBanner } from '../../features/accountManagement/MakeItYours';

const loadHomePage = () => import('../../features/home/HomePage');
const loadViewsPage = () => import('../../features/views/pages/ViewsPage');

// Keep editors and specialist pages out of the initial workspace download.
const HomePage = lazy(loadHomePage);
const ViewAsHome = lazy(() => loadHomePage().then((module) => ({ default: module.ViewAsHome })));
const NeedsYouPage = lazy(() => import('../../features/inbox/InboxPage'));
const TeamPage = lazy(() => import('../../features/agents/TeamPage'));
const AgentProfilePage = lazy(() => import('../../features/agents/AgentProfilePage'));
const AgentInitiativePage = lazy(() => import('../../features/agents/AgentInitiativePage'));
const InitiativeLinkResolver = lazy(() => import('../../features/initiatives/InitiativeLinkResolver'));
const OrgChartPage = lazy(() => import('../../features/workforce/OrgChartPage'));
const AccountPage = lazy(() => import('../../features/accountManagement/AccountPage'));
const WhatsAppLinkPage = lazy(() => import('../../features/accountManagement/WhatsAppLinkPage'));
const SendersPage = lazy(() => import('../../features/personSenders/SendersPage'));
const ContactPage = lazy(() => import('../../features/contact/ContactPage'));
const ViewsPage = lazy(loadViewsPage);
const ViewEditorPage = lazy(() => import('../../features/views/pages/ViewEditorPage'));
const BranchReviewRedirect = lazy(() => import('../../features/sandbox/components/BranchReviewRedirect'));
const IntegrationsPage = lazy(() => import('../../features/integrations/pages/IntegrationsPage'));
const McpPage = lazy(() => import('../../features/mcp/McpPage'));
const OntologyBuilderPage = lazy(() => import('../../features/ontology/ontology-builder/OntologyBuilderPage'));
const OntologyDataPage = lazy(() => import('../../features/ontology/ontology-data/OntologyDataPage'));
const OntologyActionRunsPage = lazy(() => import('../../features/ontology/action-executions/OntologyActionRunsPage'));
const AgentOrchestrationPage = lazy(() => import('../../features/automation/AgentOrchestrationPage'));
const OperationalContextPage = lazy(() => import('../../features/operationalContext/OperationalContextPage'));
const SimulationsPage = lazy(() => import('../../features/simulations/pages/SimulationsPage'));

// The most-used pages and the app host download once the shell has painted and the
// browser is idle, so opening them does not wait on the network.
function prefetchCommonPages() {
  const prefetch = () => [loadHomePage, loadViewsPage, () => import('../../features/views/code/CodeViewHost')]
    .forEach((load) => { load().catch(() => {}); });
  if (typeof window.requestIdleCallback === 'function') {
    const handle = window.requestIdleCallback(prefetch, { timeout: 5000 });
    return () => window.cancelIdleCallback(handle);
  }
  const handle = window.setTimeout(prefetch, 2000);
  return () => window.clearTimeout(handle);
}

export default function AppShell() {
  return <ViewAsProvider><AppShellContent /></ViewAsProvider>;
}

// While viewing as someone, the shell is their apps; the Superadmin's pages wait for Exit.
const VIEW_AS_PAGES = new Set(['home', 'views']);

/** Obsolete saved links resolve into the branch overlay. */
function DraftRouteRedirect() {
  const navigate = useNavigate();
  const location = useLocation();
  const destination = legacyBranchReviewDestination(location.pathname, location.search);
  useEffect(() => { if (destination) navigate(destination, { replace: true }); }, [destination, navigate]);
  return null;
}

const LEGACY_ORCHESTRATION_TABS = {
  configuration: '/app/team',
  views: '/app/views',
  programs: '/app/program-editor',
  automations: '/app/automations',
};

/** Where a retired page's link (from a sent email or bookmark) now lives. */
export function legacyDestination(page, pathname, search) {
  const params = new URLSearchParams(search);
  const segment = pathname.split('/').filter(Boolean)[2] || null;
  switch (page) {
    case 'mission-control': {
      const initiativeId = params.get('missionId');
      const askId = params.get('askId');
      if (askId) return `/app/inbox?ask=${encodeURIComponent(askId)}${initiativeId ? `&initiative=${encodeURIComponent(initiativeId)}` : ''}`;
      return initiativeId ? `/app/initiatives/${encodeURIComponent(initiativeId)}` : '/app/initiatives';
    }
    case 'missions':
      return segment ? `/app/initiatives/${encodeURIComponent(segment)}` : '/app/initiatives';
    case 'notifications': {
      const approvalId = params.get('approval');
      return approvalId ? `/app/inbox?approval=${encodeURIComponent(approvalId)}` : '/app/inbox';
    }
    case 'approvals':
      return segment ? `/app/inbox?approval=${encodeURIComponent(segment)}` : '/app/inbox';
    case 'agent-orchestration':
    case 'agent-command':
      return LEGACY_ORCHESTRATION_TABS[params.get('tab')] || '/app/initiatives';
    case 'agent-configuration':
      return '/app/team';
    case 'roles-access':
    case 'workers': {
      const roleId = params.get('role');
      return `/app/org-chart?view=roles${roleId ? `&role=${encodeURIComponent(roleId)}` : ''}`;
    }
    default:
      return '/app/home';
  }
}

function AppShellContent() {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(prefetchCommonPages, []);
  const captureViewport = useMemo(() => {
    const requested = new URLSearchParams(location.search).get('captureViewport');
    const match = requested?.match(/^(\d{3,4})x(\d{3,4})$/);
    if (!match) return null;
    const width = Number(match[1]);
    const height = Number(match[2]);
    if (width > 3840 || height > 2160) return null;
    return { width, height };
  }, [location.search]);

  const {
    userAccess,
    accessibleModules,
    isLoading: isLoadingApplicationAccess,
    error: applicationAccessError,
  } = useCurrentRoleApplicationAccess();
  const { data: orgId, isLoading: isOrgIdLoading } = useUserOrgId();
  useEffect(() => {
    if (!orgId || !userAccess?.profileId) return;
    void authFetch(`${config.API_URL}/api/account/workspace-entry`, { method: 'POST' }).catch(() => {});
  }, [orgId, userAccess?.profileId]);
  const viewer = useViewAs()?.viewer || null;
  const inViewAsFrame = isViewAsFrame();
  const accessForGating = viewerAccess(viewer, userAccess);
  // An unclaimed preconfigured account is asked to make itself the person's on every
  // page load; dismissing it lasts only as long as this shell (ADR-0044).
  const ownShell = Boolean(userAccess) && !viewer && !inViewAsFrame && !captureViewport;
  const { data: claimStatus } = useAccountClaimStatus({ enabled: ownShell });
  const [claimDismissed, setClaimDismissed] = useState(false);
  const isConfigurator = isPlatformConfigurator(accessForGating);
  const { isLoading: isLoadingViewsIndex } = useOntologyViewsIndex({
    enabled: Boolean(userAccess),
  });

  const fallbackDefaultPage = getDefaultAdminPageForRole(accessForGating);
  const onEntryPage = ['/app', '/app/', '/app/home'].includes(location.pathname);
  // The owner's first visit shows Otto setting up the workspace.
  const workspaceEntry = useWorkspaceEntry({ organizationId: orgId, profileId: userAccess?.profileId, enabled: onEntryPage });
  const setupPending = Boolean(workspaceEntry.data?.setup_pending);
  const defaultLandingPath = '/app/home';

  const legacyPage = getLegacyPageFromPath(location.pathname);
  const canAccessPage = (page) => (!viewer || VIEW_AS_PAGES.has(page)) && canAccessAdminPage(accessForGating, page);
  const requestedPage = getAdminShellPageFromPath(location.pathname);
  const hasValidRequestedPage = Boolean(requestedPage && canAccessPage(requestedPage));
  const activePage = hasValidRequestedPage && requestedPage ? requestedPage : 'home';

  const blockShellForDefaultResolution =
    Boolean(userAccess) && !hasValidRequestedPage && (isOrgIdLoading || !orgId || isLoadingViewsIndex);

  useEffect(() => {
    if (legacyPage) {
      navigate(legacyDestination(legacyPage, location.pathname, location.search), { replace: true });
      return;
    }
    if (isLoadingApplicationAccess || !fallbackDefaultPage) return;
    if (blockShellForDefaultResolution) return;
    if (legacyBranchReviewDestination(location.pathname, location.search)) return;

    if (hasValidRequestedPage) {
      const targetPath = getAdminShellPathForPage(requestedPage);
      const isWithinTargetPage =
        location.pathname === targetPath || location.pathname.startsWith(`${targetPath}/`);
      if (!isWithinTargetPage) {
        navigate(targetPath, { replace: true });
      }
      return;
    }

    const isWithinDefaultLanding =
      location.pathname === defaultLandingPath ||
      location.pathname.startsWith(`${defaultLandingPath}/`);
    if (!isWithinDefaultLanding) {
      navigate(defaultLandingPath, { replace: true });
    }
  }, [
    legacyPage,
    isLoadingApplicationAccess,
    fallbackDefaultPage,
    blockShellForDefaultResolution,
    hasValidRequestedPage,
    requestedPage,
    defaultLandingPath,
    location.pathname,
    location.search,
    navigate,
  ]);

  const handleSignOut = async () => {
    try {
      await signOut(queryClient);
    } catch (error) {
      // Handle sign out error silently in production
    }
  };

  const handleSetActivePage = (page) => {
    if (!canAccessPage(page)) {
      if (fallbackDefaultPage) {
        navigate(defaultLandingPath, { replace: true });
      }
      return;
    }
    navigate(getAdminShellPathForPage(page), page === 'contact'
      ? { state: { contactFrom: location.pathname } }
      : undefined);
  };

  const renderContent = () => {
    switch (activePage) {
      case 'home':
        return viewer ? <ViewAsHome viewer={viewer} /> : <HomePage setupPending={setupPending} />;
      case 'inbox':
        return <NeedsYouPage />;
      case 'team':
        return <TeamPage />;
      case 'agents': {
        // /app/agents/:agentId and /app/agents/:agentId/initiatives/:initiativeId
        const [, , agentId, section, initiativeId] = location.pathname.split('/').filter(Boolean);
        if (!agentId) return <Navigate replace to="/app/team" />;
        if (section === 'initiatives' && initiativeId) {
          return <AgentInitiativePage agentId={decodeURIComponent(agentId)} initiativeId={decodeURIComponent(initiativeId)} />;
        }
        return <AgentProfilePage agentId={decodeURIComponent(agentId)} />;
      }
      case 'initiatives': {
        const initiativeId = location.pathname.split('/').filter(Boolean)[2] || null;
        return <InitiativeLinkResolver initiativeId={initiativeId ? decodeURIComponent(initiativeId) : null} />;
      }
      case 'views':
        return <ViewsPage />;
      case 'sandbox':
      case 'branch-review':
        return <BranchReviewRedirect returnTo={isConfigurator ? '/app/build' : '/app/inbox'} />;
      case 'view-editor':
        return <ViewEditorPage showCatalog />;
      case 'build':
      case 'operations-map':
        return <OperationalContextPage />;
      case 'org-chart':
        return <OrgChartPage />;
      case 'account':
        return <AccountPage organizationId={orgId} />;
      case 'whatsapp':
        return <WhatsAppLinkPage />;
      case 'senders':
        return <SendersPage />;
      case 'contact':
        return <ContactPage />;
      case 'integrations':
        return <IntegrationsPage />;
      case 'mcp':
        return <McpPage />;
      case 'ontology-builder':
        return <OntologyBuilderPage />;
      case 'ontology-data':
        return <OntologyDataPage />;
      case 'ontology-action-runs':
        return <OntologyActionRunsPage />;
      case 'simulations':
        return <SimulationsPage />;
      case 'program-editor':
      case 'automations':
        return <AgentOrchestrationPage pageId={activePage} />;
      default:
        return null;
    }
  };

  const showInitialShellSpinner = !userAccess && isLoadingApplicationAccess;

  if (showInitialShellSpinner) {
    return (
      <div className="flex h-screen bg-gray-100 items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-400" />
      </div>
    );
  }

  // Operational roles are optional; the authoritative workspace profile admits a member.
  if (!userAccess) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-100 p-6">
        <div className="max-w-md rounded-xl border border-amber-200 bg-white p-6 text-center shadow-sm">
          <h2 className="text-xl font-semibold text-gray-900">Access profile unavailable</h2>
          <p className="mt-2 text-sm text-gray-600">
            We could not load your workspace access. Try again, or contact your superadmin if
            the problem continues.
          </p>
          {applicationAccessError ? (
            <p className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-700">
              {applicationAccessError.message || 'Unable to load workspace access.'}
            </p>
          ) : null}
          <div className="mt-5 flex items-center justify-center gap-3">
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
            >
              Retry
            </button>
            <button
              type="button"
              onClick={handleSignOut}
              className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Sign out
            </button>
          </div>
        </div>
      </div>
    );
  }

  const isMainContentLoading = isLoadingApplicationAccess || blockShellForDefaultResolution || Boolean(legacyPage);
  const viewAsBanner = viewer && !inViewAsFrame ? <ViewAsBanner /> : null;
  const claimBanner = ownShell && claimStatus?.preconfigured && !claimDismissed
    ? <MakeItYoursBanner onDismiss={() => setClaimDismissed(true)} />
    : null;

  if (viewAsBanner && viewer.device && viewer.device !== 'desktop') {
    return (
      <AgentPanelProvider activePage={activePage}>
        <ViewAsDeviceStage banner={viewAsBanner} device={viewer.device} target={viewer.target} />
      </AgentPanelProvider>
    );
  }

  return (
    <AgentPanelProvider activePage={activePage}>
      <WorkspaceFirstEntry activePage={activePage}>
      <DraftRouteRedirect />
      <AppShellLayout captureViewport={captureViewport} banner={viewAsBanner || claimBanner}
      sidebar={
      <AppSidebar
        activePage={activePage}
        setActivePage={handleSetActivePage}
        onNavigatePath={navigate}
        isMobileMenuOpen={isMobileMenuOpen}
        setIsMobileMenuOpen={setIsMobileMenuOpen}
        onSignOut={handleSignOut}
        userRole={viewer ? { ...accessForGating, workspaceAccess: 'apps', platformTier: 'worker', isSuperadmin: false } : accessForGating}
        enabledApplicationModules={accessibleModules}
        viewingAs={Boolean(viewer)}
      />
      } dock={captureViewport || viewer ? null : <AgentDock />}>
            <PageLoadBoundary pageKey={activePage}>
            {isMainContentLoading ? (
              <div className="flex h-full items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-400" />
              </div>
            ) : ['build', 'operations-map', 'view-editor', 'automations', 'ontology-builder', 'views'].includes(activePage) ? (
              <ConfigureSandboxPreviewChrome canOpenEditors={isConfigurator}>{renderContent()}</ConfigureSandboxPreviewChrome>
            ) : (
              renderContent()
            )}
            </PageLoadBoundary>
      </AppShellLayout>
      </WorkspaceFirstEntry>
    </AgentPanelProvider>
  );
}
